import { useEffect, useMemo, useState } from "react";
import { Trophy, AlertCircle, Sparkles } from "lucide-react";

/**
 * Admin prize-distribution editor.
 *
 * Props:
 *   - winnersCount:   number of paying ranks (1..10), driven by the parent form
 *   - prizePoolBase:  total cagnotte in € (number)
 *   - value:          [{ rank, percentage, amount? }, ...] — controlled state
 *   - onChange:       (newValue) => void
 *
 * Behaviour:
 *   - If winnersCount changes and the current array length doesn't match,
 *     we reset to the curated default for that winnersCount.
 *   - Each row shows: place label (1er, 2e, …), % input (number), € amount
 *     (computed). Admin can edit % freely; € is read-only.
 *   - We show a live status: total %, computed total €, warning if ≠ 100.
 */
const CURATED = {
  1: [100],
  2: [70, 30],
  3: [50, 30, 20],
  4: [45, 30, 15, 10],
  5: [40, 25, 15, 10, 10],
  6: [35, 22, 14, 11, 9, 9],
  7: [32, 20, 13, 10, 9, 8, 8],
  8: [30, 18, 12, 10, 9, 8, 7, 6],
  9: [28, 17, 12, 10, 9, 7, 7, 5, 5],
  10: [25, 15, 12, 10, 9, 8, 7, 6, 4, 4],
};

function defaultPercentages(n) {
  if (CURATED[n]) return [...CURATED[n]];
  if (n < 1) return [100];
  const share = Math.round((100 / n) * 100) / 100;
  const out = Array(n).fill(share);
  out[0] = Math.round((100 - share * (n - 1)) * 100) / 100;
  return out;
}

function computeAmount(pool, pct, isFirst, allPercents) {
  if (!pool || pool <= 0) return 0;
  const amt = Math.round(((pool * pct) / 100) * 100) / 100;
  if (!isFirst) return amt;
  // Rank 1 absorbs the rounding remainder so the sum equals pool exactly —
  // but ONLY when the total is already (near) 100 %. If the admin's
  // distribution is broken (sum 85 %, 110 %…) we keep the raw amount so the
  // displayed € reflects reality and isn't misleading.
  const totalPct = allPercents.reduce((acc, p) => acc + (Number(p) || 0), 0);
  if (Math.abs(totalPct - 100) > 0.01) return amt;
  const otherSum = allPercents
    .slice(1)
    .reduce((acc, p) => acc + Math.round(((pool * p) / 100) * 100) / 100, 0);
  return Math.round((pool - otherSum) * 100) / 100;
}

function placeLabel(rank) {
  return rank === 1 ? "1er" : `${rank}e`;
}

export default function PrizeDistributionEditor({
  winnersCount,
  prizePoolBase,
  value,
  onChange,
}) {
  const [rows, setRows] = useState(() =>
    Array.isArray(value) && value.length === winnersCount
      ? value.map((v, i) => ({
          rank: i + 1,
          percentage: Number(v.percentage) || 0,
        }))
      : defaultPercentages(winnersCount).map((p, i) => ({
          rank: i + 1,
          percentage: p,
        })),
  );

  // Sync rows when winnersCount changes (always reset to curated default).
  useEffect(() => {
    setRows(
      defaultPercentages(winnersCount).map((p, i) => ({
        rank: i + 1,
        percentage: p,
      })),
    );
  }, [winnersCount]);

  // Propagate via parent's onChange. We send up rank+percentage; amounts are
  // recomputed server-side anyway.
  useEffect(() => {
    onChange(rows.map((r) => ({ rank: r.rank, percentage: Number(r.percentage) || 0 })));
  }, [rows, onChange]);

  const pool = Math.max(0, Number(prizePoolBase) || 0);
  const percents = rows.map((r) => Number(r.percentage) || 0);
  const totalPct = Math.round(percents.reduce((a, b) => a + b, 0) * 100) / 100;
  const isValid = Math.abs(totalPct - 100) < 0.01 && percents.every((p) => p > 0);
  const totalAmount = useMemo(() => {
    return percents.reduce(
      (acc, p, i) => acc + computeAmount(pool, p, i === 0, percents),
      0,
    );
  }, [pool, percents]);

  const applyCurated = () => {
    setRows(
      defaultPercentages(winnersCount).map((p, i) => ({ rank: i + 1, percentage: p })),
    );
  };

  return (
    <div
      data-testid="prize-distribution-editor"
      className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-300" />
          <span className="font-display font-bold uppercase tracking-widest text-sm">
            Répartition de la cagnotte
          </span>
        </div>
        <button
          type="button"
          data-testid="prize-distribution-reset"
          onClick={applyCurated}
          className="text-[10px] uppercase tracking-widest font-mono-stat text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
        >
          <Sparkles className="w-3 h-3" /> Répartition conseillée
        </button>
      </div>

      <div className="space-y-1.5">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-white/40 font-mono-stat pb-1">
          <div className="col-span-2">Place</div>
          <div className="col-span-5">%</div>
          <div className="col-span-5 text-right">Montant estimé</div>
        </div>
        {rows.map((row, idx) => {
          const amount = computeAmount(pool, row.percentage, idx === 0, percents);
          return (
            <div
              key={row.rank}
              data-testid={`prize-row-${row.rank}`}
              className="grid grid-cols-12 gap-2 items-center"
            >
              <div className="col-span-2 font-display font-bold text-cyan-200">
                {placeLabel(row.rank)}
              </div>
              <div className="col-span-5 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus-within:border-cyan-400/60">
                <input
                  data-testid={`prize-row-${row.rank}-percentage`}
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={row.percentage}
                  onChange={(e) => {
                    const v = e.target.value === "" ? 0 : Number(e.target.value);
                    setRows((rs) =>
                      rs.map((r, i) => (i === idx ? { ...r, percentage: v } : r)),
                    );
                  }}
                  className="w-full bg-transparent outline-none font-mono-stat text-right"
                />
                <span className="text-xs text-white/40">%</span>
              </div>
              <div className="col-span-5 text-right font-mono-stat text-amber-200">
                {pool > 0 ? `${amount.toFixed(2)} €` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      <div
        data-testid="prize-distribution-total"
        className={`flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-lg font-mono-stat ${
          isValid
            ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
            : "bg-amber-500/10 text-amber-200 border border-amber-500/40"
        }`}
      >
        <span className="uppercase tracking-widest">Total</span>
        <span>
          {totalPct.toFixed(2)} % · {totalAmount.toFixed(2)} €
        </span>
      </div>
      {!isValid && (
        <div
          data-testid="prize-distribution-error"
          className="flex items-start gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2.5"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Le total de la répartition doit être égal à 100 %, sans pourcentage à 0.
            Sinon la publication du défi sera refusée.
          </span>
        </div>
      )}
    </div>
  );
}
